// Selector de rango del reporte: presets 7/30/90 días + rango personalizado.
//
// Controlado por el contenedor (HelpdeskReports): los presets se propagan tal
// cual; el rango personalizado se valida AQUÍ (from ≤ to y máximo 92 días, el
// tope del backend) y solo se propaga cuando es válido — el error se muestra
// inline, nunca como toast, para no interrumpir la exploración.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../utils/cn";
import { ymdToLocalDate } from "./format";

const MAX_RANGE_DAYS = 92;

// Presets en días. Las claves viajan al contenedor, que deriva el rango real.
// label como clave i18n completa (no interpolada) para no meter plurales.
const PRESETS = [
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "90d", label: "90 días" },
];

// Chip del segmented control. h-11 en táctil / h-8 con puntero fino: mismo
// compromiso que FilterChip en QuickRepliesSettings para cumplir la regla
// táctil global (≥44px) sin engordar el header en desktop.
function RangeChip({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center justify-center h-11 md:h-8 px-3 rounded-md text-[12px] md:text-[11px] font-medium",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active
          ? "hd-brand-solid bg-brand-500 text-white"
          : "text-fg-muted hover:text-fg hover:bg-bg-muted",
      )}
    >
      {label}
    </button>
  );
}

/**
 * @param {object} props
 * @param {string} props.preset       - '7d' | '30d' | '90d' | 'custom'
 * @param {string} props.appliedFrom  - YYYY-MM-DD del rango vigente (semilla del modo personalizado)
 * @param {string} props.appliedTo    - YYYY-MM-DD del rango vigente
 * @param {(key: string) => void} props.onSelectPreset
 * @param {(from: string, to: string) => void} props.onApplyCustom - solo se llama con rangos VÁLIDOS
 */
export default function RangePicker({ preset, appliedFrom, appliedTo, onSelectPreset, onApplyCustom }) {
  const { t } = useTranslation();

  // El panel personalizado tiene borrador local: las fechas a medio escribir
  // no deben disparar fetches ni pisar el rango aplicado.
  const [customOpen, setCustomOpen] = useState(preset === "custom");
  const [draftFrom, setDraftFrom] = useState(appliedFrom);
  const [draftTo, setDraftTo] = useState(appliedTo);
  const [error, setError] = useState(null);

  const pickPreset = (key) => {
    setCustomOpen(false);
    setError(null);
    onSelectPreset(key);
  };

  const openCustom = () => {
    // Se siembra con el rango vigente: el usuario ajusta desde algo concreto
    // en vez de partir de dos campos vacíos.
    setDraftFrom(appliedFrom);
    setDraftTo(appliedTo);
    setError(null);
    setCustomOpen(true);
  };

  // Valida y, si corresponde, aplica. Con una sola fecha completa no hay error
  // todavía: se espera la otra.
  const handleDraft = (from, to) => {
    setDraftFrom(from);
    setDraftTo(to);
    if (!from || !to) {
      setError(null);
      return;
    }
    const fromD = ymdToLocalDate(from);
    const toD = ymdToLocalDate(to);
    if (fromD > toD) {
      setError(t("La fecha inicial debe ser anterior o igual a la final."));
      return;
    }
    const days = Math.round((toD - fromD) / 86400000) + 1;
    if (days > MAX_RANGE_DAYS) {
      setError(t("El rango máximo es de 92 días."));
      return;
    }
    setError(null);
    onApplyCustom(from, to);
  };

  // GOTCHA _modern-forms.scss: sus reglas de input le ganan a Tailwind, por eso
  // las utilidades de tamaño/tipografía van con variante ! en los date inputs.
  const dateInputCls = cn(
    "h-11 md:!h-8 w-full md:w-auto !text-[16px] md:!text-[12px] !px-2 rounded-md border border-border bg-bg text-fg",
    "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
  );

  return (
    // self-start: ancla el segmented control arriba en el contenedor de
    // acciones del header (items-center). Así los chips quedan a ras con el
    // botón "Descargar CSV" vecino, que reserva altura de error debajo.
    <div className="flex flex-col items-stretch sm:items-end gap-2 self-start">
      <div
        role="group"
        aria-label={t("Rango del reporte")}
        /* A6 · En 390px los tres primeros chips llenaban la fila y "Personalizado"
            saltaba solo a una segunda línea, dejando una caja bordeada con un hueco
            a la derecha. Rejilla 2×2 de anchos iguales bajo md; en ≥768px vuelve al
            flex-wrap de siempre. */
        className="grid grid-cols-2 md:flex md:flex-wrap items-center gap-1 rounded-lg border border-border bg-bg p-1"
      >
        {PRESETS.map((p) => (
          <RangeChip
            key={p.key}
            active={!customOpen && preset === p.key}
            onClick={() => pickPreset(p.key)}
            label={t(p.label)}
          />
        ))}
        <RangeChip
          active={customOpen || preset === "custom"}
          onClick={openCustom}
          label={t("Personalizado")}
        />
      </div>

      {(customOpen || preset === "custom") && (
        <div className="flex flex-col items-stretch sm:items-end gap-1">
          <div className="flex flex-wrap items-stretch md:items-center gap-2">
            {/* A6 · Una fecha POR FILA bajo md. En dos columnas el campo mide
                128px a 360px y el ::-webkit-calendar-picker-indicator se come
                el último dígito del año ("09/08/202"). A ancho completo sobran
                >100px. En ≥768px vuelven a la fila horizontal de siempre.
                D3 · Sin `md:flex-1` ni `min-w-0` sueltos: el baseline no los
                tenía y forzaban a los dos <label> al MISMO ancho en escritorio
                («Desde» 151→154px y «Hasta» 162→154px a 1280). El `min-w-0`
                queda acotado a teléfono con `max-md:`, que es donde hace falta
                para que el input a ancho completo pueda encoger. Y el gap se
                escribe `gap-1.5 max-md:gap-1`, NO `gap-1 md:gap-1.5`: `.gap-1`
                es utilidad de Bootstrap con `!important` y le ganaba a la
                variante `md:`, encogiendo cada <label> 1,8px en escritorio
                (151→149 y 162→160 a 1280). */}
            <label className="flex flex-col items-stretch md:flex-row md:items-center gap-1.5 max-md:gap-1 w-full md:w-auto max-md:min-w-0 text-[12px] md:text-[11px] text-fg-muted">
              {t("Desde")}
              <input
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => handleDraft(e.target.value, draftTo)}
                aria-label={t("Fecha inicial")}
                aria-invalid={!!error}
                className={dateInputCls}
              />
            </label>
            <label className="flex flex-col items-stretch md:flex-row md:items-center gap-1.5 max-md:gap-1 w-full md:w-auto max-md:min-w-0 text-[12px] md:text-[11px] text-fg-muted">
              {t("Hasta")}
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => handleDraft(draftFrom, e.target.value)}
                aria-label={t("Fecha final")}
                aria-invalid={!!error}
                className={dateInputCls}
              />
            </label>
          </div>
          {error && (
            <p className="text-[12px] md:text-[11px] text-status-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
